import { useCallback, useEffect, useState } from 'react';
import { api, AdminOverview } from './adminApi';
import AdminMembers from './AdminMembers';
import AdminMatches from './AdminMatches';
import AdminPayments from './AdminPayments';
import AdminTurfs from './AdminTurfs';
import AdminMedia from './AdminMedia';
import AdminSettings from './AdminSettings';

type Section = 'overview' | 'members' | 'matches' | 'payments' | 'turfs' | 'media' | 'settings';

const NAV: Array<[Section, string]> = [
  ['overview', 'Overview'],
  ['members', 'Members'],
  ['matches', 'Matches'],
  ['payments', 'Payments'],
  ['turfs', 'Turfs'],
  ['media', 'Media'],
  ['settings', 'Settings'],
];

export default function AdminPanel() {
  const [status, setStatus] = useState<'loading' | 'ok' | 'login' | 'denied'>('loading');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [section, setSection] = useState<Section>('overview');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    const res = await api<AdminOverview>('/api/admin/overview');
    if (res.ok) {
      setOverview(res.data);
      setStatus('ok');
    } else if (res.status === 401) {
      setStatus('login');
    } else {
      setStatus('denied');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setLoginError('');
    const res = await api('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
    });
    if (res.ok) {
      setLoginPassword('');
      await load();
    } else {
      setLoginError(res.error || 'Unable to sign in');
    }
    setBusy(false);
  };

  const handleLogout = async () => {
    await api('/api/auth/logout', { method: 'POST' });
    window.location.href = '/';
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0b0f14] text-white grid place-items-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-emerald-400 border-t-transparent" />
          <p className="text-sm text-gray-400">Opening admin panel…</p>
        </div>
      </div>
    );
  }

  if (status === 'login') {
    return (
      <div className="min-h-screen bg-[#0b0f14] text-white grid place-items-center p-4">
        <form onSubmit={handleLogin} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#12181f] p-6">
          <div className="mb-6 text-center">
            <div className="text-2xl font-black tracking-tight">Giga<span className="text-emerald-400">Balls</span></div>
            <p className="mt-1 text-xs text-gray-400">Admin backend — sign in to continue</p>
          </div>
          <label className="mb-3 block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Email</span>
            <input className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-emerald-400"
              type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} />
          </label>
          <label className="mb-4 block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">Password</span>
            <input className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-emerald-400"
              type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} />
          </label>
          {loginError && <p className="mb-3 text-sm text-red-400">{loginError}</p>}
          <button className="w-full rounded-lg bg-emerald-500 py-2 text-sm font-bold text-black disabled:opacity-50"
            disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
          <a href="/" className="mt-3 block text-center text-xs text-gray-500 hover:text-gray-300">← Back to site</a>
        </form>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="min-h-screen bg-[#0b0f14] text-white grid place-items-center p-4">
        <div className="text-center">
          <div className="text-4xl">🔒</div>
          <h1 className="mt-3 text-lg font-bold">Access restricted</h1>
          <p className="mt-1 text-sm text-gray-400">Only the group owner/admin can open this panel.</p>
          <a href="/" className="mt-4 inline-block rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-black">Return to site</a>
        </div>
      </div>
    );
  }

  const metrics: Array<[string, string]> = overview ? [
    ['Members', String(overview.members)],
    ['Pending approvals', String(overview.pendingApprovals)],
    ['Matches', String(overview.matches)],
    ['Awaiting report', String(overview.awaitingReport)],
    ['Payments paid', `৳${overview.paymentsPaidTotal.toLocaleString('en-BD')}`],
    ['Turfs', String(overview.turfs)],
    ['Media', String(overview.media)],
    ['Accounts', String(overview.accounts)],
  ] : [];

  return (
    <div className="min-h-screen bg-[#0b0f14] text-gray-100">
      <div className="flex min-h-screen">
        <aside className="hidden w-60 shrink-0 border-r border-white/10 bg-[#0e1319] p-4 md:block">
          <a href="/" className="mb-6 block text-2xl font-black tracking-tight">Giga<span className="text-emerald-400">Balls</span> <span className="text-xs font-semibold text-gray-500">ADMIN</span></a>
          <nav className="space-y-1">
            {NAV.map(([id, label]) => (
              <button key={id} onClick={() => setSection(id)}
                className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold transition ${section === id ? 'bg-emerald-500/15 text-emerald-300' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </nav>
          <div className="mt-8 space-y-1 border-t border-white/10 pt-4">
            <a href="/" target="_blank" rel="noreferrer" className="block text-sm text-gray-500 hover:text-white">↗ View site</a>
            <button onClick={handleLogout} className="block text-sm text-gray-500 hover:text-white">↪ Log out</button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 p-4 md:p-6">
          <header className="mb-6 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">GigaBalls FC · backend</div>
              <h1 className="text-xl font-black">{NAV.find(([id]) => id === section)?.[1]}</h1>
            </div>
            <div className="md:hidden">
              <button onClick={handleLogout} className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-gray-400">Log out</button>
            </div>
          </header>

          {toast && (
            <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300">
              {toast} <button className="float-right" onClick={() => setToast('')}>×</button>
            </div>
          )}

          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            {metrics.map(([label, value]) => (
              <div key={label} className="rounded-xl border border-white/10 bg-[#12181f] p-4">
                <div className="text-xs text-gray-500">{label}</div>
                <div className="mt-1 text-2xl font-black">{value}</div>
              </div>
            ))}
          </div>

          <nav className="mb-6 flex gap-2 overflow-x-auto pb-1 md:hidden">
            {NAV.map(([id, label]) => (
              <button key={id} onClick={() => setSection(id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${section === id ? 'bg-emerald-500 text-black' : 'bg-white/5 text-gray-400'}`}>{label}</button>
            ))}
          </nav>

          {section === 'overview' && (
            <section className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-[#12181f] p-4">
                <h2 className="mb-3 text-sm font-bold">To-do</h2>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2"><span>Pending member approvals</span><b className="text-amber-400">{overview?.pendingApprovals || 0}</b></li>
                  <li className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2"><span>Matches awaiting report</span><b className="text-orange-400">{overview?.awaitingReport || 0}</b></li>
                  <li className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2"><span>Pending join requests</span><b className="text-sky-400">{overview?.pendingJoinRequests || 0}</b></li>
                </ul>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#12181f] p-4">
                <h2 className="mb-3 text-sm font-bold">Recent activity</h2>
                <ul className="space-y-2 text-sm">
                  {(overview?.recentActivity || []).slice(0, 8).map((item) => (
                    <li key={item.id} className="rounded-lg bg-white/5 px-3 py-2">
                      <div className="flex justify-between gap-2"><b>{item.action}</b><span className="shrink-0 text-xs text-gray-500">{(item as any).timestamp}</span></div>
                      <p className="text-xs text-gray-400">{item.details}</p>
                    </li>
                  ))}
                  {!overview?.recentActivity?.length && <li className="text-xs text-gray-500">No activity logged yet.</li>}
                </ul>
              </div>
            </section>
          )}

          {section === 'members' && <AdminMembers onToast={setToast} />}
          {section === 'matches' && <AdminMatches onToast={setToast} />}
          {section === 'payments' && <AdminPayments onToast={setToast} />}
          {section === 'turfs' && <AdminTurfs onToast={setToast} />}
          {section === 'media' && <AdminMedia onToast={setToast} />}
          {section === 'settings' && <AdminSettings onToast={setToast} />}
        </main>
      </div>
    </div>
  );
}

export const inputCls = 'w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-emerald-400';
export const btnPrimary = 'rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-bold text-black transition hover:bg-emerald-400 disabled:opacity-50';
export const btnGhost = 'rounded-lg border border-white/10 px-3 py-1.5 text-sm font-semibold text-gray-300 transition hover:bg-white/5';
export const cardCls = 'rounded-xl border border-white/10 bg-[#12181f] p-4';